(function () {
  'use strict';

  const INITIALIZED_DATASET_KEY = 'variantSortingInitialized';

  function parseJsonScript(container, selector) {
    const script = container.querySelector(selector);
    if (!script || !script.textContent.trim()) return null;

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.error('Failed to parse variant data', error);
      return null;
    }
  }

  function buildOptionEntries(variantSelects) {
    const containers = Array.from(variantSelects.querySelectorAll('.product-form__input'));

    return containers
      .map((container, index) => {
        if (container.tagName === 'FIELDSET') {
          const inputs = Array.from(container.querySelectorAll('[data-option-value-id]'));
          if (!inputs.length) return null;

          const valueMap = new Map();
          inputs.forEach((input) => {
            valueMap.set(input.value, input);
          });

          return {
            index,
            type: 'fieldset',
            container,
            inputs,
            valueMap,
          };
        }

        const select = container.querySelector('select');
        if (select) {
          const options = Array.from(select.options);
          if (!options.length) return null;

          const valueMap = new Map();
          options.forEach((option) => {
            valueMap.set(option.value, option);
          });

          return {
            index,
            type: 'select',
            container,
            select,
            options,
            valueMap,
          };
        }

        return null;
      })
      .filter(Boolean);
  }

  function buildPriceMap(sortedVariants) {
    const priceMap = new Map();

    sortedVariants.forEach((variant) => {
      if (!Array.isArray(variant.options)) return;

      variant.options.forEach((value, optionIndex) => {
        const key = `${optionIndex}||${value}`;
        if (!priceMap.has(key)) {
          priceMap.set(key, variant.price);
        }
      });
    });

    return priceMap;
  }

  function reorderEntries(entries, priceMap) {
    entries.forEach((entry) => {
      if (entry.type === 'select') {
        const orderedOptions = entry.options
          .slice()
          .sort((optionA, optionB) => {
            const priceA = priceMap.get(`${entry.index}||${optionA.value}`) ?? Number.POSITIVE_INFINITY;
            const priceB = priceMap.get(`${entry.index}||${optionB.value}`) ?? Number.POSITIVE_INFINITY;
            return priceA - priceB;
          });

        orderedOptions.forEach((option) => {
          entry.select.appendChild(option);
        });
      } else if (entry.type === 'fieldset') {
        const orderedInputs = entry.inputs
          .slice()
          .sort((inputA, inputB) => {
            const priceA = priceMap.get(`${entry.index}||${inputA.value}`) ?? Number.POSITIVE_INFINITY;
            const priceB = priceMap.get(`${entry.index}||${inputB.value}`) ?? Number.POSITIVE_INFINITY;
            return priceA - priceB;
          });

        orderedInputs.forEach((input) => {
          const label = entry.container.querySelector(`label[for="${CSS.escape(input.id)}"]`);
          entry.container.appendChild(input);
          if (label) entry.container.appendChild(label);
        });
      }
    });
  }

  function resolveTargetVariantId(sortedVariants) {
    if (!sortedVariants.length) return null;

    const url = new URL(window.location.href);
    const variantParam = url.searchParams.get('variant');
    const variantIds = sortedVariants.map((variant) => String(variant.id));

    let targetVariantId = variantIds[0];

    if (variantParam && variantIds.includes(variantParam)) {
      targetVariantId = variantParam;
    } else {
      url.searchParams.set('variant', targetVariantId);
      window.history.replaceState({}, '', url.toString());
    }

    return targetVariantId;
  }

  function selectVariant(entries, sortedVariants, targetVariantId) {
    if (!targetVariantId) return;

    const targetVariant = sortedVariants.find((variant) => String(variant.id) === String(targetVariantId));
    if (!targetVariant) return;

    let eventTarget = null;

    entries.forEach((entry) => {
      const optionValue = Array.isArray(targetVariant.options)
        ? targetVariant.options[entry.index]
        : undefined;
      if (optionValue == null) return;

      if (entry.type === 'select') {
        const optionNode = entry.valueMap.get(optionValue);
        if (!optionNode) return;

        entry.select.value = optionNode.value;
        entry.options.forEach((option) => {
          if (option === optionNode) {
            option.setAttribute('selected', 'selected');
          } else {
            option.removeAttribute('selected');
          }
        });
        eventTarget = entry.select;
      } else if (entry.type === 'fieldset') {
        const input = entry.valueMap.get(optionValue);
        if (!input) return;
        if (!input.checked) input.checked = true;
        eventTarget = input;
      }
    });

    if (eventTarget) {
      const changeEvent = new Event('change', { bubbles: true });
      eventTarget.dispatchEvent(changeEvent);
    }
  }

  function setupObserver(productInfo) {
    if (productInfo._variantSortingObserver) return;

    const observer = new MutationObserver((mutations) => {
      const storedData = productInfo._variantSorting;
      if (!storedData) return;

      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;

          if (node.matches('variant-selects')) {
            const entries = buildOptionEntries(node);
            if (entries.length) reorderEntries(entries, storedData.priceMap);
          } else {
            const nested = node.querySelector('variant-selects');
            if (!nested) return;
            const entries = buildOptionEntries(nested);
            if (entries.length) reorderEntries(entries, storedData.priceMap);
          }
        });
      });
    });

    observer.observe(productInfo, { childList: true, subtree: true });
    productInfo._variantSortingObserver = observer;
  }

  function initializeProductInfo(productInfo, enforceSelection) {
    const variantData = parseJsonScript(productInfo, 'script[data-variant-sorting]');
    if (!variantData || !Array.isArray(variantData.variants) || !variantData.variants.length) return false;

    const sortedVariants = variantData.variants.slice().sort((variantA, variantB) => variantA.price - variantB.price);
    const variantSelects = productInfo.querySelector('variant-selects');
    if (!variantSelects) return false;

    const entries = buildOptionEntries(variantSelects);
    if (!entries.length) return false;

    const priceMap = buildPriceMap(sortedVariants);
    reorderEntries(entries, priceMap);

    productInfo._variantSorting = { sortedVariants, priceMap };

    if (enforceSelection) {
      const currentVariant = parseJsonScript(productInfo, 'script[data-selected-variant]');
      const targetVariantId = resolveTargetVariantId(sortedVariants);
      if (!currentVariant || String(currentVariant.id) !== String(targetVariantId)) {
        selectVariant(entries, sortedVariants, targetVariantId);
      }
    }

    setupObserver(productInfo);

    return true;
  }

  function handleProductInfo(productInfo) {
    const enforceSelection = productInfo.dataset[INITIALIZED_DATASET_KEY] !== 'true';
    if (initializeProductInfo(productInfo, enforceSelection) && enforceSelection) {
      productInfo.dataset[INITIALIZED_DATASET_KEY] = 'true';
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('product-info').forEach(handleProductInfo);
  });

  document.addEventListener('product-info:loaded', (event) => {
    const productInfo = event.target;
    if (!(productInfo instanceof HTMLElement)) return;
    if (productInfo.tagName !== 'PRODUCT-INFO') return;
    handleProductInfo(productInfo);
  });
})();
